import { useEffect, useState, useDeferredValue, FC } from 'react';
import { Trans, t } from '@lingui/macro';
import { find, get, isEmpty } from 'lodash';
import {
  Text,
  Box,
  TextFieldSize,
  IconSize,
  FormControl,
  Select,
  TextFieldAppearance,
  Separator,
  SeparatorType,
  theme,
  Icon,
  Spinner,
  SpinnerSize,
  Heading,
  ToastType,
  ToastAction,
  useToastsDispatch,
} from '@bechtel/gds';
import { ProjectAdminContainerStyled, ProjectAdminHeaderStyled } from '~/styled';
import {
  SearchFieldComponentStyled,
  FooterStyled,
  FooterButtonStyled,
  TableStyled,
  TableHeaderStickyStyled,
  SubContainerStyled,
  TableColumnStyled,
} from './styled';
import {
  errorList,
  scheduleList,
  statusList,
  Error,
  Status,
  EventErrorTypes,
  EventStatusTypes,
  getErrorFilter,
  getStatusFilter,
  Schedule,
  notificationTypes,
} from './form-data';
import { useLazyQuery, useMutation, useApolloClient } from '@apollo/client';
import { GET_EVENT_NOTIFICATIONS, SYNC_EVENT_NOTIFICATIONS } from '@bechtel/client-gql';
import {
  EventNotificationInput,
  EventNotificationList,
  EventNotificationFilterByTerm,
  FilterOperator,
  PagingInput,
  EventNotificationFilterByTermInput,
  EventNotification,
  EventNotificationSortByTerm,
  SortOrder,
  SyncEventNotificationsInput,
  SyncEventNotificationsPayload,
  SyncEventNotificationErrorCode,
  EventNotificationStatus,
  EventNotificationTypeId,
} from '@bechtel/types';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as FilterIcon } from '@bechtel/client-assets/dist/images/filter.svg';
import { EntityStats } from './entity-stats';
import { ErrorModal, ModalError, ErrorRetry } from '@bechtel/client-common';
import { SortType, SyncRowProps } from './types';
import { getError } from './errors';
import { useTableHeaders } from './header';

const DropdownItem = ({ item }: { item: any }) => {
  return (
    <Text size="400" color="grey.1000" style={{ zIndex: 3 }}>
      {item.label}
    </Text>
  );
};

const SyncRow: FC<SyncRowProps> = ({ rowData, onCompleted, onError }) => {
  const tableHeaders = useTableHeaders();
  const [syncEventNotifications, { loading }] = useMutation<
    { syncEventNotifications: SyncEventNotificationsPayload },
    { input: SyncEventNotificationsInput }
  >(SYNC_EVENT_NOTIFICATIONS, {
    onCompleted: onCompleted,
    onError: onError,
  });

  const onSync = () => {
    syncEventNotifications({
      variables: {
        input: {
          complexFilter: {
            and: [
              {
                operator: FilterOperator.EQUALS,
                term: EventNotificationFilterByTerm.NOTIFICATION_ID,
                value: rowData.id,
              },
            ],
          },
        },
      },
    });
  };

  return (
    <tr>
      {tableHeaders.map((header, index) => (
        <TableColumnStyled key={index} sortKey={header.sortKey}>
          <Box>
            {header.render ? (
              header.render({ ...rowData, loading, onSync })
            ) : (
              <Text size="300" color={'grey.1000'}>
                {get(rowData, header.key, '')}
              </Text>
            )}
          </Box>
        </TableColumnStyled>
      ))}
    </tr>
  );
};

export const Sync = () => {
  const tableHeaders = useTableHeaders();
  const [searchQuery, setSearchQuery] = useState('');
  const debounceSearchQuery = useDeferredValue(searchQuery?.trim());
  const [selectedErrorType, setSelectedErrorType] = useState<EventErrorTypes>(Error.ALL);
  const [selectedStatus, setSelectedStatus] = useState<EventStatusTypes>(EventNotificationStatus.ERROR);
  const [selectedNotificationType, setSelectedNotificationType] = useState<EventNotificationTypeId>(
    EventNotificationTypeId.ALL
  );
  const [selectedSortBy, setSelectedSortBy] = useState<SortType>({
    sortColumn: EventNotificationSortByTerm.CREATED_AT,
    sortOrder: SortOrder.ASC,
  });
  const [hoveredColumn, setHoveredColumn] = useState<null | EventNotificationSortByTerm>(null);
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set([]));
  const [selectedScheduleSync, setScheduleSync] = useState(Schedule.NOW);
  const [syncError, setSyncError] = useState<ModalError<SyncEventNotificationErrorCode>>(null);

  const [getEventNotifications, { data, error, loading, fetchMore, refetch }] = useLazyQuery<
    { eventNotifications: EventNotificationList },
    {
      input: EventNotificationInput;
      paging?: PagingInput;
    }
  >(GET_EVENT_NOTIFICATIONS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const client = useApolloClient();
  const eventList: EventNotification[] = get(data, 'eventNotifications.items', []);
  const eventTotalRecords = get(data, 'eventNotifications.total', 0);
  const pageLimit = 12;
  const eventTotalPages = eventTotalRecords ? Math.ceil(eventTotalRecords / pageLimit) : 0;
  const eventCursor = Number(get(data, 'eventNotifications.cursor', 1));

  const dispatchToast = useToastsDispatch();
  const onCompleted = (syncData) => {
    const error = get(syncData, 'syncEventNotifications.errors.0.error');
    if (error) {
      setSyncError(error);
    } else {
      client.refetchQueries({
        include: ['eventNotifications'],
      });
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: t`Sync is Succesfull.`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
    }
  };

  const onError = (error) => {
    setSyncError(error);
  };

  const [syncEventNotifications, { loading: syncMutationLoading }] = useMutation<
    { syncEventNotifications: SyncEventNotificationsPayload },
    { input: SyncEventNotificationsInput }
  >(SYNC_EVENT_NOTIFICATIONS, {
    onCompleted: onCompleted,
    onError: onError,
  });

  useEffect(() => {
    if (debounceSearchQuery.length > 0 && debounceSearchQuery.length < 3) return;
    const input = {};
    const eventNotificationsFilter: EventNotificationFilterByTermInput[] = [];
    if (selectedErrorType && selectedErrorType !== Error.ALL) {
      eventNotificationsFilter.push(getErrorFilter(selectedErrorType));
    }
    if (selectedStatus && selectedStatus !== Status.ALL) {
      eventNotificationsFilter.push(getStatusFilter(selectedStatus));
    }
    if (selectedNotificationType && selectedNotificationType !== EventNotificationTypeId.ALL) {
      eventNotificationsFilter.push({
        operator: FilterOperator.EQUALS,
        term: EventNotificationFilterByTerm.NOTIFICATION_TYPE_ID,
        value: selectedNotificationType,
      });
    }
    if (debounceSearchQuery.length >= 3) {
      eventNotificationsFilter.push({
        operator: FilterOperator.CONTAINS,
        term: EventNotificationFilterByTerm.SEARCH_ERRORS,
        value: debounceSearchQuery,
      });
    }
    if (selectedEntities.size) {
      eventNotificationsFilter.push({
        operator: FilterOperator.IN,
        term: EventNotificationFilterByTerm.ENTITY_TYPE,
        values: Array.from(selectedEntities),
      });
    }
    if (selectedSortBy) {
      const sortByFilter = {
        term: selectedSortBy.sortColumn,
        sortOrder: selectedSortBy.sortOrder,
      };
      input['sortByFilter'] = sortByFilter;
    }
    input['complexFilter'] = {
      and: eventNotificationsFilter,
    };
    getEventNotifications({
      variables: {
        input,
        paging: {
          limit: pageLimit,
          cursor: '1',
        },
      },
    });
  }, [
    selectedErrorType,
    selectedStatus,
    selectedNotificationType,
    debounceSearchQuery,
    selectedEntities,
    selectedSortBy,
  ]);

  const onEntitySelected = (selectedEntities: Set<string>) => {
    setSelectedEntities(selectedEntities);
  };

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 50 >= e.target.scrollHeight &&
      eventCursor <= eventTotalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: eventCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    }
  };

  const onSyncAll = () => {
    const complexFilter: any = [];
    if (selectedErrorType !== Error.ALL)
      complexFilter.push({
        operator: FilterOperator.IN,
        term: EventNotificationFilterByTerm.ERROR_TYPE,
        values: [selectedErrorType],
      });
    if (selectedStatus !== Status.ALL)
      complexFilter.push({
        operator: FilterOperator.IN,
        term: EventNotificationFilterByTerm.STATUS,
        values: [selectedStatus],
      });
    if (selectedNotificationType !== EventNotificationTypeId.ALL)
      complexFilter.push({
        operator: FilterOperator.IN,
        term: EventNotificationFilterByTerm.NOTIFICATION_TYPE_ID,
        values: [selectedNotificationType],
      });
    if (debounceSearchQuery.length >= 3) {
      complexFilter.push({
        operator: FilterOperator.CONTAINS,
        term: EventNotificationFilterByTerm.SEARCH_ERRORS,
        value: debounceSearchQuery,
      });
    }
    if (complexFilter.length) {
      syncEventNotifications({
        variables: {
          input: {
            complexFilter: { and: complexFilter },
            syncScheduledAt: get(
              find(scheduleList, (item) => item.value === selectedScheduleSync),
              'datetime'
            ),
          },
        },
      });
    }
  };

  return (
    <ProjectAdminContainerStyled>
      <ProjectAdminHeaderStyled>
        <Text family="graphik" size="600" weight="600" color="white">
          <Trans>Sync</Trans>
        </Text>
      </ProjectAdminHeaderStyled>
      <SubContainerStyled>
        <Box flexDirection={{ sm: 'column' }}>
          <Box py={{ sm: '550' }} px={{ sm: '450' }} gap={{ sm: '700' }} css="width: 100%">
            <Box flexDirection={{ sm: 'column' }} gap={{ sm: '300' }}>
              <Text weight={'600'}>Sync Data</Text>
              <Text size={'300'} color="red.700">
                This may take several hours to complete
              </Text>
            </Box>
            <Box gap={{ sm: '400' }} css="width: 100%">
              <Box>
                <SearchFieldComponentStyled
                  size={TextFieldSize.LARGE}
                  iconSize={IconSize.MEDIUM}
                  placeholder={t`Search`}
                  type="search"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearchQuery(e.target.value);
                  }}
                  value={searchQuery}
                />
              </Box>
              <FormControl
                style={{ flexGrow: 1 }}
                label={
                  <Text family="graphik" size="300" color="bechtel.blue.800">
                    <Trans>Error Type</Trans>
                  </Text>
                }
              >
                <Select
                  data-testid="error-type"
                  size={TextFieldSize.MEDIUM}
                  appearance={TextFieldAppearance.NORMAL}
                  items={errorList}
                  itemComponent={DropdownItem}
                  selectedComponent={DropdownItem}
                  initialSelectedItem={errorList[0]}
                  popperStyle={{ zIndex: 2 }}
                  onSelectedItemChange={(e) => {
                    setSelectedErrorType(e.selectedItem.value as EventErrorTypes);
                  }}
                />
              </FormControl>
              <FormControl
                style={{ flexGrow: 1 }}
                label={
                  <Text family="graphik" size="300" color="bechtel.blue.800">
                    <Trans>Status</Trans>
                  </Text>
                }
              >
                <Select
                  data-testid="status"
                  size={TextFieldSize.MEDIUM}
                  appearance={TextFieldAppearance.NORMAL}
                  items={statusList}
                  itemComponent={DropdownItem}
                  selectedComponent={DropdownItem}
                  initialSelectedItem={statusList[2]}
                  popperStyle={{ zIndex: 2 }}
                  onSelectedItemChange={(e) => {
                    setSelectedStatus(e.selectedItem.value as EventStatusTypes);
                  }}
                />
              </FormControl>
              <FormControl
                style={{ flexGrow: 1 }}
                label={
                  <Text family="graphik" size="300" color="bechtel.blue.800">
                    <Trans>Notification Type</Trans>
                  </Text>
                }
              >
                <Select
                  data-testid="notificationType"
                  size={TextFieldSize.MEDIUM}
                  appearance={TextFieldAppearance.NORMAL}
                  items={notificationTypes}
                  itemComponent={DropdownItem}
                  selectedComponent={DropdownItem}
                  initialSelectedItem={notificationTypes.find((item) => {
                    return item.value === EventNotificationTypeId.ALL;
                  })}
                  popperStyle={{ zIndex: 2 }}
                  onSelectedItemChange={(e) => {
                    setSelectedNotificationType(e.selectedItem.value as EventNotificationTypeId);
                  }}
                />
              </FormControl>
            </Box>
          </Box>
          <Separator
            size="100%"
            type={SeparatorType.HORIZONTAL}
            spacing="000"
            style={{ backgroundColor: theme.colors.grey['300'] as string }}
          />
          <Box py={{ sm: '450' }} px={{ sm: '500' }} gap={{ sm: '400' }}>
            <EntityStats onEntitySelected={onEntitySelected} />
          </Box>
        </Box>
        <Box
          mt={{ sm: '500' }}
          flexDirection={{ sm: 'column' }}
          css="overflow-y:auto"
          onScroll={(e) => {
            handleScroll(e);
          }}
        >
          {!error && eventList.length > 0 && (
            <TableStyled>
              <thead data-testid="header">
                <tr>
                  {tableHeaders.map((header) => (
                    <TableHeaderStickyStyled
                      sortKey={header.sortKey}
                      data-testid={`header-${header.id}`}
                      key={header.id}
                      onMouseLeave={() => header.sortKey !== selectedSortBy.sortColumn && setHoveredColumn(null)}
                      onMouseEnter={() =>
                        header.sortKey &&
                        header.sortKey !== selectedSortBy.sortColumn &&
                        setHoveredColumn(header.sortKey)
                      }
                    >
                      <Box gap={{ sm: '100' }} alignItems={{ sm: 'center' }}>
                        <Text size="300" color={'grey.1000'}>
                          {header.label}
                        </Text>
                        {selectedSortBy.sortColumn === header.sortKey && (
                          <Icon
                            icon="sort-up"
                            size={IconSize.SMALL}
                            style={{
                              cursor: 'pointer',
                              transform: selectedSortBy.sortOrder === SortOrder.ASC ? 'rotate(180deg)' : undefined,
                            }}
                            onClick={() => {
                              setSelectedSortBy((prev) => ({
                                ...prev,
                                sortOrder: prev.sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC,
                              }));
                              setHoveredColumn(null);
                            }}
                          />
                        )}
                        {hoveredColumn === header.sortKey && (
                          <Icon
                            icon="sort-up"
                            size={IconSize.SMALL}
                            style={{
                              cursor: 'pointer',
                              transform: 'rotate(180deg)',
                            }}
                            onClick={() => {
                              setSelectedSortBy(() => ({ sortColumn: header.sortKey, sortOrder: SortOrder.ASC }));
                              setHoveredColumn(null);
                            }}
                          />
                        )}
                      </Box>
                    </TableHeaderStickyStyled>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="entity-list-container">
                {eventList.map((rowData) => (
                  <SyncRow
                    data-testid={`entity-${rowData.id}`}
                    key={rowData.id}
                    rowData={rowData}
                    onCompleted={onCompleted}
                    onError={onError}
                  />
                ))}
              </tbody>
            </TableStyled>
          )}
          {loading && (
            <Box data-testid="entity-list-loader" justifyContent={{ sm: 'center' }} css="width:100%" mt={{ sm: '400' }}>
              <Spinner size={SpinnerSize.MEDIUM} />
            </Box>
          )}
        </Box>
        {!loading && error && (
          <Box
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            flexGrow={{ sm: 1 }}
            flexDirection={{ sm: 'column' }}
            p={{ sm: '500' }}
            data-testid="user-list-retry"
          >
            <ErrorRetry refetch={refetch}>
              <Trans>There was a problem loading the events. Please click the button below to try again.</Trans>
            </ErrorRetry>
          </Box>
        )}
        {!loading && !error && !eventList.length && (
          <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }} css="width:100%">
            <Heading size="400" weight="600" color="grey.600">
              <Trans>No events found.</Trans>
            </Heading>
            <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
              <Text size="400" family="graphik" color="grey.600" style={{ textAlign: 'center', lineHeight: '1.57' }}>
                <Trans>Try changing your search or filters to view events.</Trans>
              </Text>
            </Box>
            <FilterIcon style={{ width: '124px', height: '110px' }} />
          </Box>
        )}
      </SubContainerStyled>
      <FooterStyled>
        <FormControl
          label={
            <Text family="graphik" size="300" color="bechtel.blue.800">
              <Trans>Schedule Sync</Trans>
            </Text>
          }
          css={`
            min-width: 150px;
          `}
        >
          <Select
            data-testid="schedule-sync"
            size={TextFieldSize.MEDIUM}
            appearance={TextFieldAppearance.NORMAL}
            items={scheduleList}
            itemComponent={DropdownItem}
            selectedComponent={DropdownItem}
            initialSelectedItem={scheduleList[0]}
            popperStyle={{ zIndex: 2 }}
            onSelectedItemChange={(e) => {
              setScheduleSync(e.selectedItem.value);
            }}
          />
        </FormControl>
        <FooterButtonStyled onClick={() => onSyncAll()} disabled={isEmpty(eventList)}>
          {syncMutationLoading ? (
            <Spinner />
          ) : (
            <Box gap={{ sm: '400' }}>
              <Icon size={IconSize.MEDIUM} icon="rotate" fillColor="white" />
              <Text weight="600" size="400" family="graphikCompact">
                <Trans>Sync all above</Trans>
              </Text>
            </Box>
          )}
        </FooterButtonStyled>
      </FooterStyled>
      {syncError && <ErrorModal title={t`Sync Error`} error={getError(syncError)} close={() => setSyncError(null)} />}
    </ProjectAdminContainerStyled>
  );
};
